/**
 * Returns a debounce function
 *
 * Usage:
 *```
 * // Heavywork will only be called once, after 500ms
 * const debounce = useDebouncer();
 * debounce(() => heavyWork(), 500);
 * debounce(() => heavyWork(), 500);
 * ```
 *
 * It is also possible to supply a maxWait parameter, which will force the function to execute after maxWait ms
 *```
 * const debounce = useDebouncer();
 * debounce(() => heavyWork(), 150, 200); // T: 0
 * await new Promise((r) => setTimeout(r, 100));
 * // Note: The maxWait is ignored here since the timer is already running from the first invocation
 * debounce(() => heavyWork(), 150, 200); // T: 100, the function should execute at T: 250 now since we debounced again
 * await new Promise((r) => setTimeout(r, 100));
 * // Heavywork will execute now, since we have reached the maxWait at T: 200
 * await new Promise((r) => setTimeout(r, 100));  // This takes us to T: 300, and the function should have executed but it already executed on the timeout
 * // This will start a new debouncer
 * debounce(() => heavyWork(), 100, 200);
 * // Heavywork will execute again in 100 ms
 * ```
 * @returns A debounce function
 */
export const useDebouncer = () => {
    let timeout: number = 0;
    let forceTimeout: number = 0;

    /**
     * Debounces a function
     * @param func The function to execute
     * @param wait How long to wait before we execute
     * @param maxWait How long to wait before we force the function to execute. This is optional
     */
    const debounce = (func: Function, wait: number, maxWait?: number) => {
        // If we have supplied a maxWait
        if (maxWait !== undefined && maxWait > 0) {
            // If we havent already started the forcer
            if (timeout == 0) {
                forceTimeout = window.setTimeout(() => {
                    clearTimeout(forceTimeout);
                    clearTimeout(timeout);
                    forceTimeout = 0;
                    timeout = 0;
                    func();
                }, maxWait);
            }
        }

        clearTimeout(timeout);
        timeout = window.setTimeout(() => {
            clearTimeout(forceTimeout);
            forceTimeout = 0;
            timeout = 0;
            func();
        }, wait);
    };

    return debounce;
};

/**
 * Turns a function into debounced function
 *
 * Usage:
 * ```
 * // HeavyWork will only be ran once, after 500ms
 * const debouncedFunc = convertToDebouncedFunction(() => heavyWork(), 500);
 * debouncedFunc();
 * debouncedFunc();
 * ```
 *
 * Its also possible to supply a maxWait parameter, which will force the function to execute after maxWait ms
 * ```
 * const debouncedFunc = convertToDebouncedFunction(heavyWork, 150, 200); // T: 0
 * debouncedFunc();
 * await new Promise((r) => setTimeout(r, 100));
 * debouncedFunc(); // T: 100, the function should execute at T: 250 now since we debounced again, but the maxWait is at T: 200
 * await new Promise((r) => setTimeout(r, 100));
 * // This brings us to T: 200, and the function should execute now
 * ```
 * @param func The function we want to convert to a debounced function
 * @param wait How long to wait before we execute
 * @param maxWait How long to wait before we force the function to execute. This is optional
 * @returns a function that is internally debounced
 */
export const convertToDebouncedFunction = (func: Function, wait: number, maxWait?: number) => {
    const debouncer = useDebouncer();

    return (...args: any) => debouncer(() => func(...args), wait, maxWait);
};
